import { calculateMonthlyBandwidthUsagePercentage } from '@frontend/api-client';
import type { Credit } from '@frontend/api-types';
import { type PricingMode, useFeatureIsOn, useFeatureValue } from '@frontend/react';
import { useDismissedBandwidthBannersSlice } from '@frontend/redux/hooks';
import { useCallback, useMemo } from 'react';

export const useLowBandwidthBanner = (credits?: Credit) => {
  const pricingMode = 'credit' as PricingMode;
  const { enabled: isInvisiblePricingEnabled } = useFeatureIsOn('is_invisible_pricing_enabled_for_client');
  const { value: progressDirection } = useFeatureValue('monthly_bandwidth_progress_direction', 'drain');
  const { dismissBandwidthBanner, dismissedBandwidthBanners } = useDismissedBandwidthBannersSlice();

  const usagePercentage = calculateMonthlyBandwidthUsagePercentage(credits, progressDirection);
  const isLowBandwidth = usagePercentage <= 10;

  const activeAllocationIds = useMemo(
    () => credits?.activeAllocations?.map((a) => a.id) ?? [],
    [credits?.activeAllocations],
  );

  const isDismissed = useMemo(
    () => activeAllocationIds.length > 0 && activeAllocationIds.every((id) => dismissedBandwidthBanners.includes(id)),
    [activeAllocationIds, dismissedBandwidthBanners],
  );

  const dismiss = useCallback(() => {
    activeAllocationIds.forEach((id) => dismissBandwidthBanner(id));
  }, [activeAllocationIds, dismissBandwidthBanner]);

  const shouldShowBanner = pricingMode === 'bandwidth' && isInvisiblePricingEnabled && isLowBandwidth && !isDismissed;

  return { dismiss, shouldShowBanner };
};
